import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Button } from 'shards-react';

import Constants from '../../../data/Constants';
import DocumentService from '../../../services/DocumentService';
import { useDocumentParserVersion } from '../../../hooks/queries/useDocumentParserVersion';
import { Can } from '../../../permissions/helper';

const CopyableValue = ({ value }) => {
  if (!value) return null;

  return (
    <span className="FlexAlignCenter">
      {value}
      <button
        type="button"
        className="btn btn-link btn-sm p-0 ml-2"
        title="Copiar"
        onClick={() => navigator.clipboard.writeText(value)}
      >
        <i className="fas fa-copy" />
      </button>
    </span>
  );
};

CopyableValue.propTypes = {
  value: PropTypes.string
};

const Info = ({ payment }) => {
  const [reparsing, setReparsing] = useState(false);
  const [reparseMessage, setReparseMessage] = useState(null);
  const [reparseSuccess, setReparseSuccess] = useState(null);

  const { parserVersion } = useDocumentParserVersion(payment.parsedDocumentExtId);

  const onReparse = async () => {
    if (!payment.scrapedDocumentExtId) return;

    setReparsing(true);
    setReparseMessage(null);
    setReparseSuccess(null);

    try {
      const documentService = await DocumentService();
      const result = await documentService.reparseParser([payment.scrapedDocumentExtId]);
      const documentResult = result && result[0];
      const success = documentResult && documentResult.status === 'enqueued';

      setReparseSuccess(success);
      setReparseMessage(
        success
          ? 'Reparseo encolado correctamente'
          : `Error en el reparseo${documentResult ? `: ${documentResult.status}` : ''}`
      );
    } catch {
      setReparseSuccess(false);
      setReparseMessage('Error en el reparseo');
    } finally {
      setReparsing(false);
    }
  };

  return (
    <table className="table mb-0 table-hover table-borderless">
      <tbody>
        <tr>
          <td>ID</td>
          <td>{payment.id || ''}</td>
        </tr>
        <tr>
          <td>Fecha de creación</td>
          <td>{payment.createdAt || ''}</td>
        </tr>
        <tr>
          <td>Fecha de pago</td>
          <td>{payment.paymentDate || ''}</td>
        </tr>
        <tr>
          <td>Fecha de presentación</td>
          <td>{payment.presentationDate || ''}</td>
        </tr>
        <tr>
          <td>Proveedor de información</td>
          <td className="FlexAlignCenter">
            <img
              alt={payment.infoProvider}
              className="MarginLeft10 MaxHeight50 MaxWidth100"
              src={Constants.providerLogos[payment.infoProvider]}
            />
          </td>
        </tr>
        <tr>
          <td>Tarjeta</td>
          <td>{payment.cardBrandId || ''}</td>
        </tr>
        <tr>
          <td>Tipo de documento</td>
          <td>{payment.documentType || ''}</td>
        </tr>
        <tr>
          <td>Número de liquidación</td>
          <td>{payment.clearingNumber || ''}</td>
        </tr>
        <tr>
          <td>ID de establecimiento</td>
          <td>
            {payment.establishment ? (
              <Link to={`/admin/establishments/${payment.establishment.id}`}>
                {payment.establishment.id}
              </Link>
            ) : (
              ''
            )}
          </td>
        </tr>
        <tr>
          <td>Número de establecimiento</td>
          <td>
            {payment.establishment ? (
              <Link to={`/admin/establishments/${payment.establishment.id}`}>
                {payment.establishment.number}
              </Link>
            ) : (
              ''
            )}
          </td>
        </tr>
        <tr>
          <td>Parsed Document Ext ID</td>
          <td>
            <CopyableValue value={payment.parsedDocumentExtId} />
          </td>
        </tr>
        <tr>
          <td>Scraped Document Ext ID</td>
          <td>
            <CopyableValue value={payment.scrapedDocumentExtId} />
          </td>
        </tr>
        <tr>
          <td>Versión del parseo</td>
          <td>{(parserVersion && parserVersion.processedVersion) || ''}</td>
        </tr>
        <tr>
          <td>Última versión del parser</td>
          <td>{(parserVersion && parserVersion.latestVersion) || ''}</td>
        </tr>
        <tr>
          <td />
          <td>
            <Can action="sup:reparse">
              <div className="FlexAlignCenter">
                <Button
                  disabled={reparsing || !payment.scrapedDocumentExtId}
                  onClick={onReparse}
                  size="sm"
                  theme="primary"
                >
                  {reparsing ? 'Reparseando...' : 'Reparsear'}
                </Button>
                {reparseMessage && (
                  <span className="ml-2" style={{ color: reparseSuccess ? 'green' : 'red' }}>
                    {reparseMessage}
                  </span>
                )}
              </div>
            </Can>
          </td>
        </tr>
      </tbody>
    </table>
  );
};

Info.propTypes = {
  payment: PropTypes.shape({
    id: PropTypes.string,
    createdAt: PropTypes.string,
    paymentDate: PropTypes.string,
    presentationDate: PropTypes.string,
    infoProvider: PropTypes.string,
    cardBrandId: PropTypes.string,
    documentType: PropTypes.string,
    clearingNumber: PropTypes.string,
    establishment: PropTypes.shape({
      id: PropTypes.string,
      number: PropTypes.string
    }),
    parsedDocumentExtId: PropTypes.string,
    scrapedDocumentExtId: PropTypes.string
  }).isRequired
};

export default Info;
